'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  createMasonryCellPositioner,
  type MasonryCellProps,
  type Positioner,
  Masonry as VirtualizedMasonry,
  WindowScroller,
} from 'react-virtualized';
import { Loader } from '@/app/components/ui/loader';
import type { AgentProjectShowCase } from '../../../_types';
import { LibraryProjectCard } from './LibraryProjectCard';

interface LibraryProjectGridProps {
  projectList: AgentProjectShowCase[];
  onProjectClick: (item: AgentProjectShowCase) => void;
  isLoading?: boolean;
}

// 固定卡片宽度 (w-60 = 240px)
const CARD_WIDTH = 240;
const CARD_SPACING = 16; // gap-4 = 16px

export const LibraryProjectGrid = ({ projectList, onProjectClick, isLoading }: LibraryProjectGridProps) => {
  const t = useTranslations();
  const [isMounted, setIsMounted] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const masonryRef = useRef<VirtualizedMasonry | null>(null);
  const cellPositioner = useRef<Positioner | null>(null);
  const cache = useRef<CellMeasurerCache>(
    new CellMeasurerCache({
      defaultHeight: 250,
      defaultWidth: CARD_WIDTH,
      fixedWidth: true,
    }),
  );

  useEffect(() => {
    setIsMounted(true);
    const element = document.getElementById('projectScrollableDiv');
    setScrollElement(element);
  }, []);

  // 计算列数
  const calculateColumnCount = useCallback((width: number): number => {
    return Math.max(1, Math.floor((width + CARD_SPACING) / (CARD_WIDTH + CARD_SPACING)));
  }, []);

  // 重置 Masonry 布局
  const onResize = useCallback(
    ({ width }: { width: number }) => {
      if (cellPositioner.current) {
        const columnCount = calculateColumnCount(width);
        cellPositioner.current.reset({
          columnCount,
          columnWidth: CARD_WIDTH,
          spacer: CARD_SPACING,
        });
      }
      if (masonryRef.current) {
        cache.current.clearAll();
        masonryRef.current.clearCellPositions();
      }
    },
    [calculateColumnCount],
  );

  const cellRenderer = useCallback(
    ({ index, key, parent, style }: MasonryCellProps) => {
      const item = projectList[index];
      if (!item) {
        return null;
      }
      return (
        <CellMeasurer cache={cache.current} index={index} key={key} parent={parent}>
          <div style={style} className="w-60">
            <LibraryProjectCard data={item} onClick={() => onProjectClick(item)} />
          </div>
        </CellMeasurer>
      );
    },
    [projectList, onProjectClick],
  );

  if (!isMounted || !scrollElement || (!projectList.length && isLoading)) {
    return (
      <div className="flex size-full h-[200px] flex-1 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!projectList.length) {
    return (
      <div className="flex size-full h-[200px] flex-1 items-center justify-center">
        <div className="flex h-full w-[400px] flex-col items-center justify-center lg:w-[320px]">
          <div className="mb-10 max-w-[300px] whitespace-pre text-center text-system-white64">
            {t('agentProject.noRecord')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <WindowScroller scrollElement={scrollElement}>
        {({ height, scrollTop }) => (
          <AutoSizer disableHeight onResize={onResize}>
            {({ width }) => {
              const columnCount = calculateColumnCount(width);
              if (!cellPositioner.current) {
                cellPositioner.current = createMasonryCellPositioner({
                  cellMeasurerCache: cache.current,
                  columnCount,
                  columnWidth: CARD_WIDTH,
                  spacer: CARD_SPACING,
                });
              }
              return (
                <VirtualizedMasonry
                  autoHeight
                  ref={masonryRef}
                  cellCount={projectList.length}
                  cellMeasurerCache={cache.current}
                  cellPositioner={cellPositioner.current}
                  cellRenderer={cellRenderer}
                  height={height}
                  width={width}
                  className="pb-10"
                  overscanByPixels={400}
                  scrollTop={scrollTop}
                  key={`masonry-${width}-${columnCount}`}
                />
              );
            }}
          </AutoSizer>
        )}
      </WindowScroller>
    </div>
  );
};
